import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, KeyRound, Lock, Search, ShieldCheck, Zap, ArrowRight, BookOpen, Radio, AlertTriangle, Globe } from "lucide-react";
import { useSeo } from "@/lib/seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import {
  API_BASE, API_HOST, PORTAL_URL, EXAMPLE_KEYS, GETTING_STARTED, PORTAL_MODULES, MESSAGE_LIFECYCLE,
  API_GROUPS, ENDPOINTS, STATUS_CODES, SOCKET_EVENTS,
} from "@/data/apiDocs";

const METHOD_CLS = {
  GET: "bg-sky-50 text-sky-700 ring-sky-200",
  POST: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  PUT: "bg-amber-50 text-amber-700 ring-amber-200",
  PATCH: "bg-amber-50 text-amber-700 ring-amber-200",
  DELETE: "bg-rose-50 text-rose-700 ring-rose-200",
};

function codeCls(code, active) {
  const base = code < 300 ? "emerald" : code < 500 ? "amber" : "rose";
  const map = {
    emerald: active ? "bg-emerald-600 text-white border-emerald-600" : "text-emerald-700 border-emerald-200 hover:bg-emerald-50",
    amber: active ? "bg-amber-500 text-white border-amber-500" : "text-amber-700 border-amber-200 hover:bg-amber-50",
    rose: active ? "bg-rose-600 text-white border-rose-600" : "text-rose-700 border-rose-200 hover:bg-rose-50",
  };
  return map[base];
}

const GUIDE_NAV = [
  { id: "introduction", label: "Introduction" },
  { id: "getting-started", label: "Getting started" },
  { id: "portal-modules", label: "Portal modules" },
  { id: "message-lifecycle", label: "How a message is sent" },
];
const API_NAV = [
  { id: "api-overview", label: "Overview & base URL" },
  { id: "authentication", label: "Authentication" },
  { id: "errors", label: "Errors & status codes" },
  { id: "realtime", label: "Realtime events" },
];

// ---------------------------------------------------------------------------

function CopyButton({ text, className = "" }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  };
  return (
    <button type="button" onClick={copy} aria-label="Copy to clipboard" className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-300 hover:text-white hover:bg-white/10 transition-colors ${className}`}>
      {done ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {done ? "Copied" : "Copy"}
    </button>
  );
}

function CodeBlock({ code, label }) {
  return (
    <div className="rounded-xl bg-slate-950 border border-slate-800 overflow-hidden min-w-0">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-800">
        <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
        <CopyButton text={code} />
      </div>
      <pre className="p-4 text-[12.5px] leading-relaxed text-slate-100 overflow-x-auto max-h-[420px]"><code>{code}</code></pre>
    </div>
  );
}

function Tabs({ tabs, active, onChange, renderLabel, btnCls }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="tablist">
      {tabs.map((t) => (
        <button
          key={t}
          type="button"
          role="tab"
          aria-selected={active === t}
          onClick={() => onChange(t)}
          className={btnCls ? btnCls(t, active === t) : `px-3 py-1 rounded-md text-xs font-semibold border transition-colors ${active === t ? "bg-slate-900 text-white border-slate-900" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}
        >
          {renderLabel ? renderLabel(t) : t}
        </button>
      ))}
    </div>
  );
}

const json = (v) => JSON.stringify(v, null, 2);

function buildSnippets(ep) {
  const url = `${API_BASE}${ep.examplePath || ep.path}${ep.query || ""}`;
  const headers = [];
  if (ep.auth === "live") headers.push(["X-API-Key", EXAMPLE_KEYS.live]);
  if (ep.auth === "pub") headers.push(["X-API-Key", EXAMPLE_KEYS.pub]);
  if (ep.request) headers.push(["Content-Type", "application/json"]);

  const curl = [
    `curl -X ${ep.method} "${url}"`,
    ...headers.map(([k, v]) => `  -H "${k}: ${v}"`),
    ...(ep.request ? [`  -d '${json(ep.request).replace(/'/g, "'\\''")}'`] : []),
  ].join(" \\\n");

  const hdrObj = headers.length
    ? `  headers: {\n${headers.map(([k, v]) => `    "${k}": "${v}",`).join("\n")}\n  },\n`
    : "";
  const bodyStr = ep.request ? `  body: JSON.stringify(${json(ep.request).replace(/\n/g, "\n  ")}),\n` : "";
  const js = `const res = await fetch("${url}", {\n  method: "${ep.method}",\n${hdrObj}${bodyStr}});\nconst data = await res.json();\n\nif (!res.ok) {\n  // data.detail explains what went wrong\n  throw new Error(\`\${res.status}: \${data.detail}\`);\n}\nconsole.log(data);`;

  const py = `import requests\n\nres = requests.${ep.method.toLowerCase()}(\n    "${url}",\n${headers.filter(([k]) => k !== "Content-Type").length ? `    headers={${headers.filter(([k]) => k !== "Content-Type").map(([k, v]) => `"${k}": "${v}"`).join(", ")}},\n` : ""}${ep.request ? `    json=${json(ep.request).replace(/\n/g, "\n    ").replace(/\btrue\b/g, "True").replace(/\bfalse\b/g, "False").replace(/\bnull\b/g, "None")},\n` : ""})\nprint(res.status_code, res.json())`;

  return { cURL: curl, JavaScript: js, Python: py };
}

function AuthBadge({ auth }) {
  if (auth === "none") return <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 bg-slate-100 rounded-full px-2.5 py-0.5"><Globe className="w-3 h-3" /> No API key</span>;
  if (auth === "pub") return <span className="inline-flex items-center gap-1 text-xs font-medium text-violet-700 bg-violet-50 rounded-full px-2.5 py-0.5"><KeyRound className="w-3 h-3" /> Public key · gnc_pub_</span>;
  return <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 bg-slate-100 rounded-full px-2.5 py-0.5"><Lock className="w-3 h-3" /> API key · gnc_live_</span>;
}

function Endpoint({ ep }) {
  const snippets = useMemo(() => buildSnippets(ep), [ep]);
  const [lang, setLang] = useState("cURL");
  const codes = ep.responses.map((r) => String(r.code));
  const [code, setCode] = useState(codes[0]);
  const resp = ep.responses.find((r) => String(r.code) === code) || ep.responses[0];
  const params = ep.params || [];

  return (
    <section id={ep.id} className="scroll-mt-24 py-10 border-t border-slate-200 first:border-t-0" data-testid={`docs-endpoint-${ep.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-display text-xl font-bold text-slate-900 mr-1">{ep.name}</h3>
        <AuthBadge auth={ep.auth} />
      </div>
      <div className="mt-3 flex items-center gap-2 min-w-0 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
        <span className={`shrink-0 text-[11px] font-bold rounded px-2 py-0.5 ring-1 ${METHOD_CLS[ep.method]}`}>{ep.method}</span>
        <code className="text-sm text-slate-800 truncate">/api/v1{ep.path}</code>
      </div>
      <p className="mt-4 text-slate-600 leading-relaxed">{ep.summary}</p>

      {ep.notes?.length > 0 && (
        <ul className="mt-4 space-y-2">
          {ep.notes.map((n) => (
            <li key={n} className="flex gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{n}</span>
            </li>
          ))}
        </ul>
      )}

      {params.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-semibold text-slate-900">{ep.request ? "Request body" : "Parameters"}</h4>
          <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-3 py-2 font-semibold">Field</th><th className="hidden sm:table-cell px-3 py-2 font-semibold">Type</th><th className="px-3 py-2 font-semibold">Description</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {params.map((p) => (
                  <tr key={p.name} className="align-top">
                    <td className="px-3 py-2 sm:whitespace-nowrap break-words max-w-[9rem] sm:max-w-none">
                      <code className="text-[13px] font-semibold text-slate-900">{p.name}</code>
                      <div className="sm:hidden text-[11px] text-slate-500"><code>{p.type}</code></div>
                      {p.in && p.in !== "body" && <span className="ml-1.5 text-[10px] uppercase text-slate-400">{p.in}</span>}
                      <div className={`text-[11px] font-medium ${p.required ? "text-rose-600" : "text-slate-400"}`}>{p.required ? "required" : "optional"}</div>
                    </td>
                    <td className="hidden sm:table-cell px-3 py-2 text-slate-500 whitespace-nowrap"><code className="text-xs">{p.type}</code></td>
                    <td className="px-3 py-2 text-slate-600">{p.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-6 grid xl:grid-cols-2 gap-5">
        <div className="min-w-0">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h4 className="text-sm font-semibold text-slate-900">Request</h4>
            <Tabs tabs={Object.keys(snippets)} active={lang} onChange={setLang} />
          </div>
          <CodeBlock code={snippets[lang]} label={lang} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h4 className="text-sm font-semibold text-slate-900">Response</h4>
            <Tabs
              tabs={codes}
              active={code}
              onChange={setCode}
              btnCls={(t, a) => `px-2.5 py-1 rounded-md text-xs font-bold border transition-colors ${codeCls(Number(t), a)}`}
            />
          </div>
          <div className={`mb-2 text-sm rounded-lg px-3 py-2 ${resp.na ? "bg-slate-50 text-slate-500 italic" : "bg-slate-50 text-slate-700"}`}>
            <span className="font-semibold not-italic text-slate-900">{resp.code} {resp.title}</span> — {resp.when}
          </div>
          <CodeBlock code={json(resp.body)} label={`${resp.code} · application/json`} />
        </div>
      </div>
    </section>
  );
}

function SectionTitle({ id, eyebrow, title, children }) {
  return (
    <div id={id} className="scroll-mt-24">
      {eyebrow && <div className="text-xs uppercase tracking-widest text-[#25D366] font-semibold mb-2">{eyebrow}</div>}
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{title}</h2>
      {children && <p className="mt-3 text-slate-600 leading-relaxed">{children}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function DocsPage() {
  useSeo({
    title: "Docs & API Reference – WhatsApp Business API | GrabNChat",
    description: "How the GrabNChat portal works, plus the REST API reference for WhatsApp messages, templates, contacts, leads and analytics with example responses.",
  });

  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState("introduction");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ENDPOINTS;
    return ENDPOINTS.filter((e) => `${e.name} ${e.path} ${e.method} ${e.group}`.toLowerCase().includes(q));
  }, [query]);

  // Highlight the sidebar entry for the section currently on screen.
  useEffect(() => {
    const ids = [...GUIDE_NAV, ...API_NAV].map((n) => n.id).concat(ENDPOINTS.map((e) => e.id));
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!("IntersectionObserver" in window) || !els.length) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-80px 0px -65% 0px" },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [filtered]);

  const navCls = (id) => `block truncate rounded-md px-2.5 py-1.5 text-sm transition-colors ${activeId === id ? "bg-[#25D366]/10 text-slate-900 font-semibold" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"}`;

  const sidebar = (
    <nav aria-label="Documentation" className="space-y-6">
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter endpoints…"
          className="w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 py-2 text-sm outline-none focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/20"
          data-testid="docs-search"
        />
      </div>
      {!query && (
        <>
          <div>
            <div className="px-2.5 mb-1 text-[11px] uppercase tracking-wider font-semibold text-slate-400">Portal guide</div>
            {GUIDE_NAV.map((n) => <a key={n.id} href={`#${n.id}`} className={navCls(n.id)}>{n.label}</a>)}
          </div>
          <div>
            <div className="px-2.5 mb-1 text-[11px] uppercase tracking-wider font-semibold text-slate-400">API basics</div>
            {API_NAV.map((n) => <a key={n.id} href={`#${n.id}`} className={navCls(n.id)}>{n.label}</a>)}
          </div>
        </>
      )}
      {API_GROUPS.map((g) => {
        const items = filtered.filter((e) => e.group === g);
        if (!items.length) return null;
        return (
          <div key={g}>
            <div className="px-2.5 mb-1 text-[11px] uppercase tracking-wider font-semibold text-slate-400">{g}</div>
            {items.map((e) => (
              <a key={e.id} href={`#${e.id}`} className={`${navCls(e.id)} flex items-center gap-2`}>
                <span className={`shrink-0 w-11 text-center text-[10px] font-bold rounded px-1 py-0.5 ring-1 ${METHOD_CLS[e.method]}`}>{e.method}</span>
                <span className="truncate">{e.name}</span>
              </a>
            ))}
          </div>
        );
      })}
      {query && !filtered.length && <p className="px-2.5 text-sm text-slate-500">No endpoints match “{query}”.</p>}
    </nav>
  );

  return (
    <div data-testid="docs-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white border-b border-slate-200/60">
        <div className="absolute inset-0 bg-dots opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-10 pb-12">
          <Breadcrumbs items={[{ name: "Docs & API", path: "/docs" }]} className="mb-10" />
          <div className="text-xs uppercase tracking-widest text-[#25D366] font-semibold mb-3">Documentation</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">GrabNChat Docs & API Reference</h1>
          <p className="mt-4 text-lg text-slate-600 max-w-3xl">Learn how the GrabNChat portal works, then connect your own apps, website and backend to WhatsApp with our REST API.</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="#getting-started" className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1ebe5a] transition-colors"><BookOpen className="w-4 h-4" /> Portal guide</a>
            <a href="#api-overview" className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 transition-colors"><Zap className="w-4 h-4" /> API reference</a>
            <a href={`${PORTAL_URL}/app/api-integration`} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors"><KeyRound className="w-4 h-4" /> Get an API key</a>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-[250px_minmax(0,1fr)] lg:gap-10">
          {/* Sidebar (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto py-8 pr-2">{sidebar}</div>
          </aside>

          {/* Mobile jump menu */}
          <div className="lg:hidden pt-6">
            <label htmlFor="docs-jump" className="sr-only">Jump to section</label>
            <select
              id="docs-jump"
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm"
              value=""
              onChange={(e) => { if (e.target.value) window.location.hash = e.target.value; }}
            >
              <option value="">Jump to section…</option>
              <optgroup label="Portal guide">{GUIDE_NAV.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}</optgroup>
              <optgroup label="API basics">{API_NAV.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}</optgroup>
              {API_GROUPS.map((g) => (
                <optgroup key={g} label={g}>
                  {ENDPOINTS.filter((e) => e.group === g).map((e) => <option key={e.id} value={e.id}>{e.method} {e.name}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="min-w-0 py-10 space-y-16">
            {/* ------------------------------------------------ Portal guide */}
            <section className="space-y-6">
              <SectionTitle id="introduction" eyebrow="Portal guide" title="What is GrabNChat?">
                GrabNChat is a multi-channel customer-engagement portal built on the official WhatsApp Business (Cloud) API. Your team uses the web portal at{" "}
                <a href={PORTAL_URL} className="font-semibold text-[#25D366] hover:underline">{PORTAL_URL.replace(/^https?:\/\//, "")}</a>{" "}
                to chat with customers, run campaigns and automate replies across WhatsApp, Instagram, Facebook Messenger and your website — and your developers use the REST API to do the same from your own systems.
              </SectionTitle>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { icon: ShieldCheck, t: "Official Meta API", d: "Messages go through the WhatsApp Cloud API from your own verified number." },
                  { icon: Zap, t: "Portal + API", d: "Everything sent via the API also appears live in the Team Inbox." },
                  { icon: Radio, t: "Realtime", d: "Socket.IO events for new messages and delivery status." },
                ].map((c) => (
                  <div key={c.t} className="rounded-2xl border border-slate-200 p-5">
                    <c.icon className="w-5 h-5 text-[#25D366]" />
                    <div className="mt-3 font-semibold text-slate-900">{c.t}</div>
                    <p className="mt-1 text-sm text-slate-600">{c.d}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="space-y-6">
              <SectionTitle id="getting-started" title="Getting started">Go from sign-up to your first WhatsApp message in six steps.</SectionTitle>
              <ol className="grid sm:grid-cols-2 gap-4">
                {GETTING_STARTED.map((s, i) => (
                  <li key={s.title} className="flex gap-4 rounded-2xl border border-slate-200 p-5">
                    <span className="shrink-0 w-8 h-8 rounded-full bg-[#25D366] text-white grid place-items-center text-sm font-bold">{i + 1}</span>
                    <div>
                      <div className="font-semibold text-slate-900">{s.title}</div>
                      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <section className="space-y-6">
              <SectionTitle id="portal-modules" title="Portal modules">What each section of the portal's left-hand menu does.</SectionTitle>
              {PORTAL_MODULES.map((g) => (
                <div key={g.group}>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{g.group}</h3>
                  <div className="mt-3 grid sm:grid-cols-2 gap-3">
                    {g.items.map((m) => (
                      <div key={m.name} className="rounded-xl border border-slate-200 p-4">
                        <div className="font-semibold text-slate-900">{m.name}</div>
                        <p className="mt-1 text-sm text-slate-600 leading-relaxed">{m.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </section>

            <section className="space-y-6">
              <SectionTitle id="message-lifecycle" title="How a message is sent">The same pipeline runs whether you send from the portal or the API.</SectionTitle>
              <ol className="relative border-l-2 border-[#25D366]/30 ml-3 space-y-5">
                {MESSAGE_LIFECYCLE.map((s, i) => (
                  <li key={s.step} className="pl-6 relative">
                    <span className="absolute -left-[11px] top-0.5 w-5 h-5 rounded-full bg-[#25D366] text-white grid place-items-center text-[10px] font-bold">{i + 1}</span>
                    <div className="font-semibold text-slate-900">{s.step}</div>
                    <p className="text-sm text-slate-600">{s.text}</p>
                  </li>
                ))}
              </ol>
              <div className="rounded-xl bg-sky-50 border border-sky-200 p-4 text-sm text-sky-900">
                <strong>24-hour window:</strong> free-form text can only be sent within 24 hours of the customer's last message. To start a conversation, or after the window closes, send an approved template.
              </div>
            </section>

            {/* ------------------------------------------------ API basics */}
            <section className="space-y-6">
              <SectionTitle id="api-overview" eyebrow="API reference" title="Overview & base URL">
                The GrabNChat API is a JSON REST API. Every request and response body is JSON, and every error has the same shape: an object with a human-readable <code className="text-slate-900">detail</code> string.
              </SectionTitle>
              <CodeBlock label="Base URL" code={API_BASE} />
              <ul className="grid sm:grid-cols-2 gap-3 text-sm">
                <li className="rounded-xl border border-slate-200 p-4"><strong className="text-slate-900">HTTPS only.</strong> <span className="text-slate-600">Plain-HTTP requests are not supported in production.</span></li>
                <li className="rounded-xl border border-slate-200 p-4"><strong className="text-slate-900">Phone numbers</strong> <span className="text-slate-600">in international format with country code, e.g. +919999999999.</span></li>
                <li className="rounded-xl border border-slate-200 p-4"><strong className="text-slate-900">Timestamps</strong> <span className="text-slate-600">are ISO-8601 UTC strings, e.g. 2026-09-29T10:15:42.118Z.</span></li>
                <li className="rounded-xl border border-slate-200 p-4"><strong className="text-slate-900">Versioned.</strong> <span className="text-slate-600">/api/v1 is stable — breaking changes ship under a new version.</span></li>
              </ul>
            </section>

            <section className="space-y-6">
              <SectionTitle id="authentication" title="Authentication">
                Authenticate by sending your API key in the <code className="text-slate-900">X-API-Key</code> header (or <code className="text-slate-900">Authorization: ApiKey &lt;key&gt;</code>). Create and revoke keys in the portal under Manage → API Integration. A key is shown only once, when it is created.
              </SectionTitle>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-200 p-5">
                  <div className="flex items-center gap-2 font-semibold text-slate-900"><Lock className="w-4 h-4" /> Secret key — <code>gnc_live_…</code></div>
                  <p className="mt-2 text-sm text-slate-600">Full API access for your company. Keep it on your server or in your mobile app's secure storage — never in website HTML or browser JavaScript.</p>
                </div>
                <div className="rounded-2xl border border-slate-200 p-5">
                  <div className="flex items-center gap-2 font-semibold text-slate-900"><KeyRound className="w-4 h-4" /> Public key — <code>gnc_pub_…</code></div>
                  <p className="mt-2 text-sm text-slate-600">Can only create leads via <a href="#capture-lead" className="text-[#25D366] font-semibold">/leads/capture</a>. Safe to embed in website forms.</p>
                </div>
              </div>
              <CodeBlock label="Header" code={`X-API-Key: ${EXAMPLE_KEYS.live}`} />
            </section>

            <section className="space-y-6">
              <SectionTitle id="errors" title="Errors & status codes">
                Check the HTTP status code first. For any non-2xx status, the body is <code className="text-slate-900">{"{ \"detail\": \"…\" }"}</code> explaining what went wrong.
              </SectionTitle>
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                    <tr><th className="px-3 py-2 font-semibold">Code</th><th className="px-3 py-2 font-semibold">Meaning</th><th className="px-3 py-2 font-semibold">When</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {STATUS_CODES.map((s) => (
                      <tr key={s.code}>
                        <td className="px-3 py-2"><span className={`inline-block rounded-md border px-2 py-0.5 text-xs font-bold ${codeCls(s.code, false)}`}>{s.code}</span></td>
                        <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{s.name}</td>
                        <td className="px-3 py-2 text-slate-600">{s.text}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <CodeBlock label="Error response" code={json({ detail: "Missing required field: to" })} />
                <CodeBlock label="Unknown URL (404)" code={json({ detail: "Not Found", path: "/api/v1/unknown" })} />
              </div>
            </section>

            <section className="space-y-6">
              <SectionTitle id="realtime" title="Realtime events">
                Connect with Socket.IO to receive new messages and delivery statuses the moment they happen. Authenticate the socket with the same API key.
              </SectionTitle>
              <CodeBlock
                label="JavaScript (socket.io-client)"
                code={`import { io } from "socket.io-client";\n\nconst socket = io("${API_HOST}", {\n  path: "/api/socket.io",\n  transports: ["websocket"],\n  auth: { api_key: "${EXAMPLE_KEYS.live}" },\n});\n\nsocket.on("ready", (info) => console.log("connected", info));\nsocket.on("whatsapp_message", (msg) => console.log("message", msg));\nsocket.on("latest_status", (s) => console.log("status", s.wp_status));`}
              />
              <div className="grid sm:grid-cols-2 gap-3">
                {SOCKET_EVENTS.map((e) => (
                  <div key={e.name} className="rounded-xl border border-slate-200 p-4">
                    <code className="text-sm font-semibold text-slate-900">{e.name}</code>
                    <p className="mt-1 text-sm text-slate-600">{e.text}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* ------------------------------------------------ Endpoints */}
            {API_GROUPS.map((g) => {
              const items = filtered.filter((e) => e.group === g);
              if (!items.length) return null;
              return (
                <section key={g}>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{g}</h2>
                  {items.map((ep) => <Endpoint key={ep.id} ep={ep} />)}
                </section>
              );
            })}

            <section className="rounded-3xl bg-slate-900 text-white p-8 sm:p-10">
              <h2 className="font-display text-2xl font-bold">Need help integrating?</h2>
              <p className="mt-2 text-slate-300">Our team can help you connect your CRM, ERP, e-commerce store or custom app to WhatsApp.</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1ebe5a]">Contact support <ArrowRight className="w-4 h-4" /></Link>
                <a href={`${PORTAL_URL}/login`} className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-4 py-2.5 text-sm font-semibold hover:bg-white/10">Log in to the portal</a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
